/**
 * Every SEO tag the blog emits, built in one place.
 *
 * This module is imported by BOTH the Vue app and the Node prerender script,
 * and that sharing is the entire point. A crawler reads the prerendered head;
 * a reader clicking from the listing to an article gets tags applied
 * client-side. If those were built by two different code paths they would
 * drift, and the drift would be invisible - the page looks fine either way,
 * and only a scraper notices the tags are wrong.
 *
 * Everything here is pure: no DOM, no network, no module-level state, so it
 * runs identically under Node and in a browser. The DOM side lives in
 * `@/composables/useBlogSeo`.
 */

import { PUBLIC_PAGES } from '../config/publicPages.js';

export const SITE_NAME = 'sortmyprep';
export const DEFAULT_SITE_URL = 'https://sortmyprep.com';

/**
 * The site's share card, served from `public/`. Marketing pages always use it;
 * an article falls back to it when it has no hero of its own, since the hero is
 * optional and a share with no image at all renders as a bare link.
 */
export const DEFAULT_OG_IMAGE = {
  path: '/og-image.png',
  width: 1200,
  height: 630,
  alt: SITE_NAME,
};

const listingPage = PUBLIC_PAGES.find((page) => page.path === '/blogs');
export const LISTING_TITLE = listingPage.title;
export const LISTING_DESCRIPTION = listingPage.description;

/** Marketing pages that were previously hand-maintained in public/sitemap.xml. */
export const STATIC_SITEMAP_URLS = PUBLIC_PAGES.map(
  ({ path, changefreq, priority }) => ({ path, changefreq, priority })
);

const trimSlash = (value) => String(value || '').replace(/\/+$/, '');

/** Resolve a path or already-absolute URL against the site root. */
export const absoluteUrl = (pathOrUrl, siteUrl = DEFAULT_SITE_URL) => {
  const value = String(pathOrUrl || '');
  if (/^https?:\/\//i.test(value)) return value;
  if (!value) return trimSlash(siteUrl) || siteUrl;
  return `${trimSlash(siteUrl)}/${value.replace(/^\/+/, '')}`;
};

export const blogUrl = (slug, siteUrl = DEFAULT_SITE_URL) =>
  `${trimSlash(siteUrl)}/blogs/${slug}`;

export const listingUrl = (siteUrl = DEFAULT_SITE_URL) =>
  `${trimSlash(siteUrl)}/blogs`;

/** Escape a value for use inside a double-quoted HTML attribute. */
export const escapeAttribute = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** og/twitter image tags for the site's default share card. */
const defaultImageTags = (siteUrl) => {
  const image = absoluteUrl(DEFAULT_OG_IMAGE.path, siteUrl);
  return [
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: String(DEFAULT_OG_IMAGE.width) },
    { property: 'og:image:height', content: String(DEFAULT_OG_IMAGE.height) },
    { property: 'og:image:alt', content: DEFAULT_OG_IMAGE.alt },
    { name: 'twitter:image', content: image },
  ];
};

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

/**
 * The head for one article, or for the listing when `post` is omitted.
 *
 * `meta_title` falls back to the title and `meta_description` to the excerpt,
 * so a published article can never carry empty meta no matter what the author
 * left blank.
 */
export const buildMetaTags = (post, siteUrl = DEFAULT_SITE_URL) => {
  if (!post) {
    const url = listingUrl(siteUrl);
    return {
      title: LISTING_TITLE,
      meta: [
        { name: 'title', content: LISTING_TITLE },
        { name: 'description', content: LISTING_DESCRIPTION },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: SITE_NAME },
        { property: 'og:url', content: url },
        { property: 'og:title', content: LISTING_TITLE },
        { property: 'og:description', content: LISTING_DESCRIPTION },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: LISTING_TITLE },
        { name: 'twitter:description', content: LISTING_DESCRIPTION },
        { name: 'robots', content: 'index, follow' },
        ...defaultImageTags(siteUrl),
      ],
      link: [{ rel: 'canonical', href: url }],
    };
  }

  const title = post.meta_title || post.title || '';
  const description = post.meta_description || post.excerpt || '';
  const url = blogUrl(post.slug, siteUrl);
  const image = post.image ? absoluteUrl(post.image, siteUrl) : '';

  const meta = [
    { name: 'title', content: title },
    { name: 'description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:url', content: url },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'robots', content: 'index, follow' },
  ];

  if (image) {
    meta.push({ property: 'og:image', content: image });
    meta.push({ name: 'twitter:image', content: image });
    // Width and height let a scraper lay the card out before the image
    // downloads, which is the difference between a large card and a thumbnail.
    if (post.image_width) {
      meta.push({
        property: 'og:image:width',
        content: String(post.image_width),
      });
    }
    if (post.image_height) {
      meta.push({
        property: 'og:image:height',
        content: String(post.image_height),
      });
    }
    if (post.image_alt) {
      meta.push({ property: 'og:image:alt', content: post.image_alt });
    }
  } else {
    // The author's image_alt is not reused here: it describes a hero that is
    // not there, not the card being shown instead.
    meta.push(...defaultImageTags(siteUrl));
  }

  if (post.published_at) {
    meta.push({
      property: 'article:published_time',
      content: post.published_at,
    });
  }
  if (post.modified) {
    meta.push({ property: 'article:modified_time', content: post.modified });
  }
  // Open Graph treats article:section as a single value, so the first category
  // stands for the article. The full set goes into JSON-LD below, which does
  // accept a list.
  const sections = (post.categories ?? [])
    .map((category) => category?.name)
    .filter(Boolean);
  if (sections.length) {
    meta.push({ property: 'article:section', content: sections[0] });
  }

  return { title, meta, link: [{ rel: 'canonical', href: url }] };
};

/** Head for a slug that does not resolve - kept out of the index. */
export const buildNotFoundMetaTags = () => ({
  title: 'Article not found - sortmyprep',
  meta: [{ name: 'robots', content: 'noindex' }],
  link: [],
});

const breadcrumbList = (post, siteUrl) => {
  const items = [
    { name: 'Home', item: `${trimSlash(siteUrl)}/` },
    { name: 'Blogs', item: listingUrl(siteUrl) },
  ];
  if (post) {
    items.push({ name: post.title, item: blogUrl(post.slug, siteUrl) });
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  };
};

/**
 * Structured data for an article, or just the breadcrumb for the listing.
 *
 * The breadcrumb changes nothing visually but makes Google render a trail in
 * the result instead of a bare URL, which measurably helps click-through.
 */
export const buildJsonLd = (post, siteUrl = DEFAULT_SITE_URL) => {
  if (!post) return [breadcrumbList(null, siteUrl)];

  const url = blogUrl(post.slug, siteUrl);
  const posting = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    headline: post.title,
    description: post.meta_description || post.excerpt || '',
    author: { '@type': 'Person', name: post.author_name || SITE_NAME },
    publisher: { '@type': 'Organization', name: SITE_NAME },
  };

  // Google recommends an image for article rich results; the brand card keeps
  // an image-less post eligible rather than dropping it from them.
  posting.image = absoluteUrl(post.image || DEFAULT_OG_IMAGE.path, siteUrl);
  if (post.published_at) posting.datePublished = post.published_at;
  if (post.modified) posting.dateModified = post.modified;
  const sections = (post.categories ?? [])
    .map((category) => category?.name)
    .filter(Boolean);
  // schema.org allows a list here, unlike og:article:section.
  if (sections.length) posting.articleSection = sections;

  return [posting, breadcrumbList(post, siteUrl)];
};

/** Render a head description into HTML for the prerender step. */
export const renderHead = ({ title, meta = [], link = [] }, jsonLd = []) => {
  const parts = [];
  if (title) parts.push(`<title>${escapeAttribute(title)}</title>`);

  for (const tag of meta) {
    const key = tag.name ? 'name' : 'property';
    const value = tag.name || tag.property;
    parts.push(
      `<meta ${key}="${escapeAttribute(value)}" content="${escapeAttribute(tag.content)}">`
    );
  }

  for (const tag of link) {
    parts.push(
      `<link rel="${escapeAttribute(tag.rel)}" href="${escapeAttribute(tag.href)}">`
    );
  }

  for (const block of jsonLd) {
    // `<` is escaped so a title containing "</script>" cannot break out of
    // the block it is embedded in.
    const json = JSON.stringify(block).replace(/</g, '\\u003c');
    parts.push(
      `<script type="application/ld+json" data-page-seo>${json}</script>`
    );
  }

  return parts.join('\n');
};

const MANAGED_OPEN = '<!-- blog-seo -->';
const MANAGED_CLOSE = '<!-- /blog-seo -->';

/**
 * Put a rendered head fragment into an HTML document.
 *
 * Tags the fragment with markers and replaces any previous one, so running
 * this twice over the same document does not accumulate duplicates. Existing
 * `<title>` and description/og tags from index.html are dropped, since the
 * article's own values must win.
 */
export const injectHead = (html, headHtml) => {
  const wrapped = `${MANAGED_OPEN}\n${headHtml}\n${MANAGED_CLOSE}`;

  // The trailing `\s*` matters: without it each pass leaves behind the newline
  // that followed the block it removed, so re-injecting grows a blank line
  // every time and the output is never byte-identical to the previous run.
  let output = html.replace(
    new RegExp(`${MANAGED_OPEN}[\\s\\S]*?${MANAGED_CLOSE}\\s*`),
    ''
  );

  output = output
    .replace(/<title>[\s\S]*?<\/title>\s*/i, '')
    .replace(/<meta\s+name="(?:title|description)"[^>]*>\s*/gi, '')
    .replace(/<meta\s+property="og:[^"]*"[^>]*>\s*/gi, '')
    .replace(/<meta\s+name="twitter:[^"]*"[^>]*>\s*/gi, '')
    .replace(/<meta\s+name="robots"[^>]*>\s*/gi, '')
    .replace(/<link\s+rel="canonical"[^>]*>\s*/gi, '');

  if (output.includes('</head>')) {
    return output.replace('</head>', `${wrapped}\n</head>`);
  }
  return `${wrapped}\n${output}`;
};

/** sitemap.xml covering the marketing pages plus every published article. */
export const buildSitemap = (posts = [], siteUrl = DEFAULT_SITE_URL) => {
  const entries = STATIC_SITEMAP_URLS.map((entry) => ({
    loc: absoluteUrl(entry.path, siteUrl),
    changefreq: entry.changefreq,
    priority: entry.priority,
    lastmod: null,
  }));

  for (const post of posts) {
    entries.push({
      loc: blogUrl(post.slug, siteUrl),
      changefreq: 'monthly',
      priority: '0.6',
      lastmod: post.modified || post.published_at || null,
    });
  }

  const body = entries
    .map((entry) => {
      const lines = [`    <loc>${escapeXml(entry.loc)}</loc>`];
      if (entry.lastmod) {
        lines.push(
          `    <lastmod>${escapeXml(String(entry.lastmod).slice(0, 10))}</lastmod>`
        );
      }
      lines.push(`    <changefreq>${entry.changefreq}</changefreq>`);
      lines.push(`    <priority>${entry.priority}</priority>`);
      return `  <url>\n${lines.join('\n')}\n  </url>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${body}\n</urlset>\n`;
};

/**
 * Firebase redirect entries, one per retired slug.
 *
 * A retitle moves an article's URL. Without these the old URL 404s, the
 * ranking it accumulated is lost and every already-shared link breaks.
 */
export const buildRedirects = (posts = []) => {
  const redirects = [];
  for (const post of posts) {
    for (const previous of post.previous_slugs || []) {
      // A post that reclaimed its own old slug would otherwise redirect to
      // itself, which is a loop rather than a redirect.
      if (previous === post.slug) continue;
      redirects.push({
        source: `/blogs/${previous}`,
        destination: `/blogs/${post.slug}`,
        type: 301,
      });
    }
  }
  return redirects;
};

/**
 * A crawlable list of article links, for the prerendered listing body.
 *
 * The prerender injects meta into the head, which is all a social scraper
 * reads - but a search crawler that does not run JS would otherwise find an
 * empty `<div id="app">` and no way to reach a single article. The sitemap
 * offers discovery; this offers the internal links that carry ranking with
 * them, so the archive is one hop from a page already indexed.
 *
 * Vue replaces the mount point's contents on boot, so a human never sees this.
 */
export const renderListingLinks = (posts = [], siteUrl = DEFAULT_SITE_URL) => {
  if (!posts.length) return '';
  const items = posts
    .map((post) => {
      const href = escapeAttribute(blogUrl(post.slug, siteUrl));
      const title = escapeAttribute(post.title || post.slug);
      return `<li><a href="${href}">${title}</a></li>`;
    })
    .join('\n');
  return `<nav aria-label="All articles">\n<ul>\n${items}\n</ul>\n</nav>`;
};

/**
 * Put a body fragment inside the app mount point.
 *
 * Marked like the head fragment so a second pass replaces rather than stacks.
 */
/**
 * The article itself, for the prerendered page body.
 *
 * The head alone was the original contract, on the reasoning that a social
 * scraper reads nothing else and Googlebot renders the JS. Both halves are
 * true and neither was the whole cost: rendering is a second pass on a separate
 * queue, it couples indexing to the API being up at a moment nobody controls,
 * an article had zero internal links until it ran, and crawlers that execute no
 * JavaScript at all - which is most of the AI ones - saw an empty div.
 *
 * `blog-body` is doing real work here. `assets/main.css` styles an article
 * entirely through that class and ships in the render-blocking stylesheet, so
 * this fragment has correct typography before the bundle runs. The container
 * width is the one thing inlined, because `max-w-[760px]` is a JIT utility that
 * exists only while `BlogDetailView` references it.
 *
 * The body is embedded verbatim: it was sanitised server-side against a tag
 * allowlist before it was ever stored.
 */
export const renderArticle = (post, siteUrl = DEFAULT_SITE_URL) => {
  if (!post) return '';

  const parts = [
    `<a href="${escapeAttribute(listingUrl(siteUrl))}">All articles</a>`,
    `<h1>${escapeAttribute(post.title ?? '')}</h1>`,
  ];

  if (post.excerpt) parts.push(`<p>${escapeAttribute(post.excerpt)}</p>`);

  if (post.image) {
    const attrs = [
      `src="${escapeAttribute(absoluteUrl(post.image, siteUrl))}"`,
      `alt="${escapeAttribute(post.image_alt || post.title || '')}"`,
    ];
    // Dimensions reserve the space before the file arrives. Omitted rather than
    // guessed when the API has none, since a wrong ratio shifts the page more
    // than no ratio does.
    if (post.image_width) attrs.push(`width="${post.image_width}"`);
    if (post.image_height) attrs.push(`height="${post.image_height}"`);
    parts.push(`<img ${attrs.join(' ')}>`);
  }

  if (post.body) parts.push(`<div class="blog-body">${post.body}</div>`);

  // The links that make an article a route onward rather than a dead end. The
  // listing already offers a path in; before this, nothing led back out.
  const categories = (post.categories ?? []).filter((c) => c?.slug);
  if (categories.length) {
    const links = categories
      .map((category) => {
        const href = `${listingUrl(siteUrl)}?category=${encodeURIComponent(category.slug)}`;
        return `<li><a href="${escapeAttribute(href)}">${escapeAttribute(category.name || category.slug)}</a></li>`;
      })
      .join('\n');
    parts.push(`<nav aria-label="Categories">\n<ul>\n${links}\n</ul>\n</nav>`);
  }

  return `<main style="max-width:760px;margin:0 auto;padding:0 24px">\n${parts.join('\n')}\n</main>`;
};

/**
 * The same post as JSON, for the app to render from without waiting on the API.
 *
 * `<` is escaped to its unicode form throughout. A body containing a closing
 * script tag would otherwise end this element early and spill the rest of the
 * article into the document as markup - the one way author content can escape a
 * JSON island.
 */
export const renderPayload = (post) => {
  if (!post) return '';
  const json = JSON.stringify(post).replace(/</g, '\\u003c');
  return `<script type="application/json" id="blog-data">${json}</script>`;
};

/**
 * Put the payload just before `</body>`, deliberately outside the mount point.
 *
 * Vue empties `#app` when it mounts, and it does so before any route component
 * runs its setup - so a payload injected into the mount point would already be
 * gone by the time the article page looked for it. Outside it, the element
 * survives the mount and the app can read it whenever it is ready.
 */
export const injectPayload = (html, payloadHtml) => {
  if (!payloadHtml) return html;
  const open = '<!-- blog-data -->';
  const close = '<!-- /blog-data -->';
  const wrapped = `${open}${payloadHtml}${close}`;

  const stripped = html.replace(new RegExp(`${open}[\\s\\S]*?${close}`), '');
  return stripped.replace('</body>', `${wrapped}</body>`);
};

export const injectBody = (html, bodyHtml) => {
  if (!bodyHtml) return html;
  const open = '<!-- blog-links -->';
  const close = '<!-- /blog-links -->';
  const wrapped = `${open}${bodyHtml}${close}`;

  const stripped = html.replace(new RegExp(`${open}[\\s\\S]*?${close}`), '');
  return stripped.replace(
    /(<div id="app">)(\s*)(<\/div>)/,
    (match, start, _gap, end) => `${start}${wrapped}${end}`
  );
};
